import { User } from "../models/auth.models";

export interface RefreshOutcome {
  user: User | null;
  errorMessage: string | null;
  transient: boolean;
}

let inFlight: Promise<RefreshOutcome> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

const REFRESH_QUERY = `
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      refreshToken
      expired
      username
      mobile
      email
      status
      type
      roleName
      roleId
      userId
      district
      sessionStart
    }
  }
`;

export function cancelScheduledRefresh(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

export function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem("loggedInUser");
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token?: string): { iat?: number; exp?: number } | null {
  const part = token?.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Delay until half of the access-token lifetime remains. */
export function halfLifeDelayMs(accessToken?: string, nowMs = Date.now()): number {
  const payload = decodeJwtPayload(accessToken);
  const now = nowMs / 1000;
  if (!payload?.exp) return 5000;
  const iat = typeof payload.iat === "number" ? payload.iat : now;
  const midpoint = iat + (payload.exp - iat) / 2;
  return Math.max((midpoint - now) * 1000, 5000);
}

export function terminalRefreshFailure(message: string | null): boolean {
  const text = (message || "").toLowerCase();
  return (
    text.includes("refresh token revoked") ||
    text.includes("inactive") ||
    text.includes("deactivated")
  );
}

async function requestRefresh(apiUrl: string): Promise<RefreshOutcome> {
  const stored = readStoredUser();
  const refreshToken = stored?.refreshToken;
  if (!refreshToken) {
    return { user: null, errorMessage: "Refresh token revoked", transient: false };
  }
  try {
    const res = await fetch(`${apiUrl}/graphql`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apollo-require-preflight": "true",
      },
      credentials: "include",
      body: JSON.stringify({
        query: REFRESH_QUERY,
        variables: { refreshToken },
      }),
    });
    const json = await res.json();
    const user = json?.data?.refreshToken ?? null;
    const errorMessage = json?.errors?.[0]?.message ?? null;
    if (user?.accessToken && user?.refreshToken) {
      return { user, errorMessage: null, transient: false };
    }
    if (terminalRefreshFailure(errorMessage)) {
      return { user: null, errorMessage, transient: false };
    }
    return { user: null, errorMessage, transient: true };
  } catch {
    return { user: null, errorMessage: null, transient: true };
  }
}

export function refreshAccessToken(
  apiUrl: string,
  apply?: (user: User) => void,
): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;
  const run = async () => {
    const outcome = await requestRefresh(apiUrl);
    if (outcome.user?.refreshToken && apply) {
      apply(outcome.user);
    }
    return outcome;
  };
  const locks = typeof navigator !== "undefined" ? (navigator as Navigator & {
    locks?: { request: (name: string, options: { mode: string }, callback: () => Promise<RefreshOutcome>) => Promise<RefreshOutcome> };
  }).locks : undefined;
  const pending = locks?.request
    ? locks.request("ctms-token-refresh", { mode: "exclusive" }, run)
    : run();
  inFlight = pending.finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Refreshes once half of the access-token lifetime has passed.
 * A transient failure tries again later. A revoked refresh token ends the session.
 */
export function scheduleAccessRefresh(
  user: User | null,
  apiUrl: string,
  onRefreshed: (user: User) => void,
  onTerminal: () => void,
): void {
  cancelScheduledRefresh();
  if (!user?.refreshToken) return;
  const fire = (delay: number) => {
    timer = setTimeout(async () => {
      timer = null;
      if (!readStoredUser()?.refreshToken) return;
      const outcome = await refreshAccessToken(apiUrl, (next) => {
        if (!readStoredUser()) return;
        onRefreshed(next);
      });
      if (!readStoredUser()) return;
      if (outcome.user?.refreshToken) return;
      if (terminalRefreshFailure(outcome.errorMessage)) {
        onTerminal();
        return;
      }
      fire(60_000);
    }, delay);
  };
  fire(halfLifeDelayMs(user.accessToken));
}
